import { useState } from 'react';
import { ERROR_CODE } from '../_constants/apiConst';
import { I18nMessage } from '../types/type';
import { ERROR_CODE_MAP } from '../locales/errorCodeMapping';
import { useTranslation } from 'react-i18next';
import { CustomError } from '../services/CustomError';

const useErrorModal = () => {
  const [error, setError] = useState<string | I18nMessage>();
  const [onClose, setOnClose] = useState<() => void>();
  const { t } = useTranslation();

  /** カスタムエラー表示できるようにdataを整形する */
  const getCustomErrorValues = (data: string[]) => {
    const values: Record<string, string> = {};
    data.forEach((value, index) => {
      values[index] = value;
    });
    return values;
  };

  const hideError = () => {
    setError(undefined);
    if (onClose) {
      onClose();
    }
    setOnClose(undefined);
  };

  /** メッセージ表示 */
  const showClientError = (
    message: string | I18nMessage,
    onCloseCallback: () => void = hideError,
  ) => {
    setError(message);
    setOnClose(() => onCloseCallback);
  };

  /** カスタムエラーの表示 */
  const showCustomError = (
    error: CustomError,
    onCloseCallback: () => void = hideError,
  ) => {
    const { code, data } = error;
    const values = getCustomErrorValues(data);
    const message = {
      messageKey: ERROR_CODE_MAP[code],
      values: values,
    };
    setError(message);
    setOnClose(() => onCloseCallback);
  };

  /** APIエラー用メッセージ表示 */
  const showApiError = (
    apiError: unknown,
    apiCode: string,
    onCloseCallback: () => void = hideError,
  ) => {
    if (!(apiError instanceof CustomError) && !(apiError instanceof Error)) {
      return;
    }

    // カスタムエラー
    if (apiError instanceof CustomError) {
      showCustomError(apiError, onCloseCallback);
      return;
    }

    const statusCode = Number(apiError.message);
    // 共通エラーハンドリング
    switch (statusCode) {
      case ERROR_CODE.UNAUTHORIZED:
        setError('ERR_SESSION');
        return;
      case ERROR_CODE.TIMEOUT:
        setError('ERR_REQUEST_TIMEOUT');
        return;
      default:
        setError({ messageKey: 'ERR_UNEXPECTED_FE', values: { 0: apiCode } });
    }
    setOnClose(() => onCloseCallback);
  };

  /** タイプエラー用メッセージ表示 */
  const showTypeError = (apiCode: string) => {
    setError({ messageKey: 'ERR_INVALID_TYPE', values: { 0: apiCode } });
    return;
  };

  /** APIエラーメッセージを取得 */
  const getApiErrorMessage = (apiError: unknown, apiCode: string) => {
    // カスタムエラー
    if (apiError instanceof CustomError) {
      const { code, data } = apiError;
      const values = getCustomErrorValues(data);
      return t(ERROR_CODE_MAP[code], values);
    }
    if (apiError instanceof Error) {
      const statusCode = Number(apiError.message);
      // 共通エラーハンドリング
      switch (statusCode) {
        case ERROR_CODE.UNAUTHORIZED:
          return t('ERR_SESSION');
        case ERROR_CODE.TIMEOUT:
          return t('ERR_REQUEST_TIMEOUT');
        default:
          return t('ERR_UNEXPECTED_FE', { 0: apiCode });
      }
    }
    return '';
  };

  /** 型エラーメッセージを取得 */
  const getTypeErrorMessage = (apiCode: string) => {
    return t('ERR_INVALID_TYPE', { 0: apiCode });
  };

  return {
    error,
    hideError,
    showApiError,
    showClientError,
    showTypeError,
    getApiErrorMessage,
    getTypeErrorMessage,
  };
};

export default useErrorModal;
